import API_URL from "../apiBase";

const TOKEN_KEY = "ctrl_admin_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

async function request(path, { method = "GET", body, auth = true } = {}) {
    const headers = {};
    const token = getToken();
    if (auth && token) headers.Authorization = `Bearer ${token}`;

    let payload = body;
    const isForm = body instanceof FormData;
    if (body && !isForm) {
        headers["Content-Type"] = "application/json";
        payload = JSON.stringify(body);
    }

    const res = await fetch(`${API_URL}${path}`, {
        method,
        headers,
        body: payload,
    });

    let data = null;
    try {
        data = await res.json();
    } catch {
        /* no body */
    }

    if (!res.ok) {
        const err = new Error((data && data.error) || `HTTP ${res.status}`);
        err.status = res.status;
        throw err;
    }
    return data;
}

export const api = {
    // auth
    login: (email, password) =>
        request("/api/auth/login", {
            method: "POST",
            body: { email, password },
            auth: false,
        }),
    me: () => request("/api/auth/me"),

    // stats / analytics
    stats: () => request("/api/admin/stats"),
    analytics: () => request("/api/admin/analytics"),
    myAnalytics: () => request("/api/admin/my-analytics"),

    // users
    listUsers: (params = {}) => {
        const qs = new URLSearchParams(
            Object.entries(params).filter(([, v]) => v !== "" && v != null)
        ).toString();
        return request(`/api/admin/users?${qs}`);
    },
    createUser: (body) => request("/api/admin/users", { method: "POST", body }),
    updateUser: (id, body) =>
        request(`/api/admin/users/${id}`, { method: "PUT", body }),
    deleteUser: (id) => request(`/api/admin/users/${id}`, { method: "DELETE" }),

    // groups
    listGroups: (params = {}) => {
        const qs = new URLSearchParams(
            Object.entries(params).filter(([, v]) => v !== "" && v != null)
        ).toString();
        return request(`/api/admin/groups?${qs}`);
    },
    createGroup: (body) => request("/api/admin/groups", { method: "POST", body }),
    updateGroup: (id, body) =>
        request(`/api/admin/groups/${id}`, { method: "PUT", body }),
    deleteGroup: (id) => request(`/api/admin/groups/${id}`, { method: "DELETE" }),
    listGroupAdmins: (id) => request(`/api/admin/groups/${id}/admins`),
    addGroupAdmin: (id, email) =>
        request(`/api/admin/groups/${id}/admins`, {
            method: "POST",
            body: { email },
        }),
    removeGroupAdmin: (id, userId) =>
        request(`/api/admin/groups/${id}/admins/${userId}`, { method: "DELETE" }),

    // my group (self profile edit)
    myGroup: () => request("/api/admin/my-group"),
    updateMyGroup: (body) =>
        request("/api/admin/my-group", { method: "PUT", body }),

    // notifications
    listNotifications: () => request("/api/admin/notifications"),
    sendNotification: (body) =>
        request("/api/admin/notifications", { method: "POST", body }),
    // certificates (emails sent)
    listCertificates: (params = {}) => {
        const qs = new URLSearchParams(
            Object.entries(params).filter(([, v]) => v !== undefined && v !== "")
        ).toString();
        return request(`/api/admin/certificates${qs ? `?${qs}` : ""}`);
    },
    sendCertificates: (eventId) =>
        request(`/api/admin/certificates/send/${eventId}`, { method: "POST" }),
    sendTestEmail: (to) =>
        request(`/api/admin/certificates/test`, { method: "POST", body: { to } }),

    listNotificationTemplates: () =>
        request("/api/admin/notifications/templates"),
    updateNotificationTemplate: (key, body) =>
        request(`/api/admin/notifications/templates/${key}`, {
            method: "PUT",
            body,
        }),
    resetNotificationTemplate: (key) =>
        request(`/api/admin/notifications/templates/${key}/reset`, {
            method: "POST",
        }),

    // feedback (public contact form submissions)
    listFeedback: () => request("/api/admin/feedback"),
    markFeedbackRead: (id, read = true) =>
        request(`/api/admin/feedback/${id}`, { method: "PATCH", body: { read } }),
    deleteFeedback: (id) =>
        request(`/api/admin/feedback/${id}`, { method: "DELETE" }),

    // reports / moderation (shared "reports" collection from the mobile app)
    listReports: (params = {}) => {
        const qs = new URLSearchParams(
            Object.entries(params).filter(([, v]) => v !== "" && v != null)
        ).toString();
        return request(`/api/admin/reports${qs ? `?${qs}` : ""}`);
    },
    getReport: (id) => request(`/api/admin/reports/${id}`),
    setReportStatus: (id, status) =>
        request(`/api/admin/reports/${id}`, {
            method: "PATCH",
            body: { status },
        }),
    deleteReportedContent: (id) =>
        request(`/api/admin/reports/${id}/delete-content`, { method: "POST" }),
    banReportTarget: (id, disabled = true) =>
        request(`/api/admin/reports/${id}/ban-target`, {
            method: "POST",
            body: { disabled },
        }),

    // organization partner applications
    listOrganizations: (params = {}) => {
        const qs = new URLSearchParams(
            Object.entries(params).filter(([, v]) => v !== "" && v != null)
        ).toString();
        return request(`/api/admin/organizations${qs ? `?${qs}` : ""}`);
    },
    setOrganizationStatus: (id, status) =>
        request(`/api/admin/organizations/${id}`, {
            method: "PATCH",
            body: { status },
        }),
    deleteOrganization: (id) =>
        request(`/api/admin/organizations/${id}`, { method: "DELETE" }),

    // my team
    myTeam: () => request("/api/admin/my-team"),
    setMemberRole: (userId, role) =>
        request(`/api/admin/my-team/members/${userId}/role`, {
            method: "POST",
            body: { role },
        }),
    removeTeamMember: (userId) =>
        request(`/api/admin/my-team/members/${userId}`, { method: "DELETE" }),

    // my team — applicants (people who applied to join; accept or reject)
    myTeamApplicants: () => request("/api/admin/my-team/applicants"),
    acceptTeamApplicant: (userId) =>
        request(`/api/admin/my-team/applicants/${userId}/accept`, {
            method: "POST",
        }),
    rejectTeamApplicant: (userId) =>
        request(`/api/admin/my-team/applicants/${userId}/reject`, {
            method: "POST",
        }),

    // events
    listEvents: (params = {}) => {
        const qs = new URLSearchParams(
            Object.entries(params).filter(([, v]) => v !== "" && v != null)
        ).toString();
        return request(`/api/admin/events?${qs}`);
    },
    createEvent: (body) => request("/api/admin/events", { method: "POST", body }),
    getEventAttendees: (id) => request(`/api/admin/events/${id}/attendees`),
    updateEvent: (id, body) =>
        request(`/api/admin/events/${id}`, { method: "PUT", body }),
    deleteEvent: (id) =>
        request(`/api/admin/events/${id}`, { method: "DELETE" }),
    bulkDeleteEvents: (ids) =>
        request("/api/admin/events/bulk-delete", { method: "POST", body: { ids } }),
    acceptApplicant: (id, userId) =>
        request(`/api/admin/events/${id}/applicants/${userId}/accept`, {
            method: "POST",
        }),
    rejectApplicant: (id, userId) =>
        request(`/api/admin/events/${id}/applicants/${userId}/reject`, {
            method: "POST",
        }),
    unacceptParticipant: (id, userId) =>
        request(`/api/admin/events/${id}/participants/${userId}/unaccept`, {
            method: "POST",
        }),
    setParticipantNote: (id, userId, note) =>
        request(`/api/admin/events/${id}/participants/${userId}/note`, {
            method: "PUT",
            body: { note },
        }),
    setEventLead: (id, userId, lead) =>
        request(`/api/admin/events/${id}/participants/${userId}/lead`, {
            method: "POST",
            body: { lead },
        }),

    // content
    listNews: () => request("/api/admin/news"),
    createNews: (body) => request("/api/admin/news", { method: "POST", body }),
    updateNews: (id, body) =>
        request(`/api/admin/news/${id}`, { method: "PUT", body }),
    deleteNews: (id) => request(`/api/admin/news/${id}`, { method: "DELETE" }),

    // portfolio (public website Events-page gallery)
    listPortfolio: () => request("/api/admin/portfolio"),
    createPortfolio: (body) =>
        request("/api/admin/portfolio", { method: "POST", body }),
    updatePortfolio: (id, body) =>
        request(`/api/admin/portfolio/${id}`, { method: "PUT", body }),
    deletePortfolio: (id) =>
        request(`/api/admin/portfolio/${id}`, { method: "DELETE" }),

    // gifts (CTRL coin rewards; shared with the mobile app)
    listGifts: () => request("/api/admin/gifts"),
    createGift: (body) => request("/api/admin/gifts", { method: "POST", body }),
    updateGift: (id, body) =>
        request(`/api/admin/gifts/${id}`, { method: "PUT", body }),
    deleteGift: (id) => request(`/api/admin/gifts/${id}`, { method: "DELETE" }),
    listGiftClaims: (params = {}) => {
        const qs = new URLSearchParams(
            Object.entries(params).filter(([, v]) => v !== "" && v != null)
        ).toString();
        return request(`/api/admin/gifts/claims${qs ? `?${qs}` : ""}`);
    },
    deliverGiftClaim: (id) =>
        request(`/api/admin/gifts/claims/${id}/deliver`, { method: "PATCH" }),
    undeliverGiftClaim: (id) =>
        request(`/api/admin/gifts/claims/${id}/undeliver`, { method: "PATCH" }),

    listPosts: () => request("/api/admin/posts"),
    listMyPosts: () => request("/api/admin/my-posts"),
    createPost: (body) => request("/api/admin/posts", { method: "POST", body }),
    updatePost: (id, body) =>
        request(`/api/admin/posts/${id}`, { method: "PUT", body }),
    deletePost: (id) => request(`/api/admin/posts/${id}`, { method: "DELETE" }),

    // upload
    uploadImage: (file) => {
        const fd = new FormData();
        fd.append("image", file);
        return request("/api/admin/upload", { method: "POST", body: fd });
    },
};

export default api;
